import type { AddonPrice, DiscountType } from '@/logged-in/components/Contracts/types'
import type { IconId } from '@/shared/components/Icon'
import type { RichTextDocument } from '@/shared/components/RichText'
import type { ItemStatus } from '@/shared/graphql/schema/commonBackend/graphql'
import type {
  EnrichedAvailableAddon,
  EnrichedBundledAddonTemplate,
  EnrichedContractTemplate,
} from '@/shared/services/campaignDataResolver'
import type { SelectedAddons } from '@/shared/store/slices/selectedContractSlice'

export const ADDON_TYPE = {
  hourlyMeasurement: 'hourlyMeasurement',
}

export const isHourlyMeasurementAddon = (addonType: string) =>
  addonType === ADDON_TYPE.hourlyMeasurement

/**
 * Method that filter out all addons that are not available under upgrade flow
 */
export const filterOutSpecialAddons = (addons: EnrichedAvailableAddon[]) =>
  addons.filter((addon) => !isHourlyMeasurementAddon(addon.addonType))

export const filterOnlySpecialAddons = (addons: EnrichedAvailableAddon[]) =>
  addons.filter((addon) => isHourlyMeasurementAddon(addon.addonType))

export type GlobalAddonItem = {
  /**
   * Addon tariffNo.
   */
  tariffNo: number
  /**
   * Addon templateNo.
   */
  templateNo?: number | null
  /**
   * Addon type.
   */
  addonType: string
  options: {
    /**
     * If true addon is bundled into contract.
     */
    isBundled: boolean
    /**
     * Set to true to indicate that addon should be handled as hourly measurement checkbox.
     */
    isHourlyMeasurementAddon: boolean
  }
  // That is only temporary element which we will remove as soon as we agree on common type for AddonInformation
  enrichedData: EnrichedAvailableAddon | EnrichedBundledAddonTemplate
}

export type AddonItem = {
  /**
   * Addon tariffNo.
   */
  tariffNo: number
  /**
   * Addon templateNo.
   */
  templateNo?: number | null
  /**
   * Addon status.
   */
  status?: ItemStatus
  /**
   * In most cases addon name.
   */
  primaryText: string
  /**
   * Price information.
   */
  secondaryText: string
  /**
   * Additional information as richText
   */
  additionalInformation?: RichTextDocument | null
  /**
   * Description as richText
   */
  description?: string | null
  /**
   * Termination selling points as richText
   */
  terminationSellingPoints?: (string | null)[] | null
  /**
   * Selling points as richText
   */
  sellingPoints?: (string | null)[] | null
  /**
   * Addon icon.
   */
  icon: IconId
  /**
   * If true addon is bundled into contract.
   */
  isBundled: boolean
  /**
   * Set to true to indicate that addon should be handled as hourly measurement checkbox.
   */
  isHourlyMeasurementAddon: boolean
  /**
   * Addon type.
   */
  addonType: string
  urlTerms?: string
  urlMoreInfo?: string | null
  /**
   * used in logged-in to indicate that addon is ordered until processed by backend
   */
  isOrdered?: boolean
  /**
   * To check if addon is cancelable
   */
  isAddonCancellable?: boolean
  /**
   * To check if addon has been cancelled
   */
  isCancelled?: boolean
  contractNo?: number
  monthly?: AddonPrice
  energy?: AddonPrice
  discountType?: DiscountType
  contentName?: string
}

export const getAddonItem = (
  addon?: EnrichedAvailableAddon | EnrichedBundledAddonTemplate,
): GlobalAddonItem | null =>
  addon?.tariffNo
    ? {
        enrichedData: addon,
        tariffNo: addon.tariffNo,
        addonType: addon.addonType,
        options: {
          isBundled: !Boolean((addon as EnrichedAvailableAddon).templateNo),
          isHourlyMeasurementAddon: isHourlyMeasurementAddon(addon.addonType) ?? false,
        },
      }
    : null

const isAddonItem = (item: unknown): item is GlobalAddonItem =>
  Boolean((item as GlobalAddonItem).tariffNo)

const shouldBeShown = (addon: GlobalAddonItem) =>
  addon.options.isHourlyMeasurementAddon || addon.options.isBundled

export const getBundledOrPreselectedAddons = (contractTemplate?: EnrichedContractTemplate) =>
  contractTemplate
    ? [...contractTemplate.availableAddons, ...contractTemplate.bundledAddons]
        .map(getAddonItem)
        .filter(isAddonItem)
        .filter(shouldBeShown)
    : []

export const getAllAvailableAddons = (contractTemplate?: EnrichedContractTemplate) =>
  contractTemplate
    ? [...contractTemplate.availableAddons, ...contractTemplate.bundledAddons]
        .map(getAddonItem)
        .filter(isAddonItem)
    : []

export const areAddonsSelected = (
  contractAddons: EnrichedAvailableAddon[],
  selectedAddons: SelectedAddons,
) => {
  const filteredAddons = filterOutSpecialAddons(contractAddons)
  return filteredAddons.every((addon) => Boolean(selectedAddons[addon.addonType]))
}
